import { useState } from "react";
import { Plus } from "lucide-react";
import { useMarket } from "../lib/store";
import { useSubscribe, useTick } from "../lib/socket";
import { useBasket } from "../lib/basket";
import { useUnderlyings, useChain } from "../lib/useChain";
import { maxPain } from "../lib/options";
import { fmt, compact, pct, tone } from "../lib/format";
import { Panel, PageHeader, Empty } from "../components/Panel";
import { SkeletonRows } from "../components/motion";
import { Price } from "../components/Price";

const sel = "h-8 px-2 rounded-md border border-input bg-card text-[12px] outline-none focus:border-primary";

function Side({ k, itm, right }) {
  const t = useTick(k);
  const openOrder = useMarket((s) => s.openOrder);
  const add = useBasket((s) => s.add);
  const g = t?.greeks || {};
  const cells = [
    <td key="oi" className="px-2 num text-right">{compact(t?.oi)}</td>,
    <td key="v" className="px-2 num text-right text-muted-foreground">{compact(t?.v)}</td>,
    <td key="iv" className="px-2 num text-right">{t?.iv ? (t.iv > 1 ? t.iv : t.iv * 100).toFixed(1) : "—"}</td>,
    <td key="d" className="px-2 num text-right text-muted-foreground">{g.delta ? g.delta.toFixed(2) : "—"}</td>,
    <td key="th" className="px-2 num text-right text-muted-foreground hidden xl:table-cell">{g.theta ? g.theta.toFixed(1) : "—"}</td>,
    <td key="ltp" className="px-2 text-right group/c relative">
      <span className={tone(t?.ch)}>{t?.ltp ? <Price value={t.ltp} prev={t.prevLtp} /> : "—"}</span>
      <div className={`text-[9.5px] num ${tone(t?.ch)}`}>{t?.ltp ? pct(t?.chp) : ""}</div>
      <span className="absolute inset-y-0 left-0 hidden group-hover/c:flex items-center gap-0.5 bg-card/95 px-1">
        <button data-testid={`chain-buy-${k}`} onClick={() => openOrder({ key: k, side: "BUY" })} className="h-5 w-5 rounded bg-up text-white text-[9px] font-bold">B</button>
        <button data-testid={`chain-sell-${k}`} onClick={() => openOrder({ key: k, side: "SELL" })} className="h-5 w-5 rounded bg-down text-white text-[9px] font-bold">S</button>
        <button data-testid={`chain-basket-${k}`} onClick={() => add({ key: k, side: "BUY" })} className="h-5 w-5 rounded border border-border grid place-items-center"><Plus className="h-3 w-3" /></button>
      </span>
    </td>,
  ];
  if (right) cells.reverse();
  return <>{cells.map((c, i) => <c.type key={i} {...c.props} className={`${c.props.className} ${itm ? "bg-amber-500/[0.12]" : ""}`} />)}</>;
}

export default function OptionChain() {
  const unds = useUnderlyings();
  const [und, setUnd] = useState("NSE_INDEX|Nifty 50");
  const [win, setWin] = useState(12);
  const { expiries, expiry, setExpiry, rows, atm, spot, lot, chain } = useChain(und, win);
  const ticks = useMarket((s) => s.ticks);
  const keys = rows.flatMap((r) => [r.CE, r.PE]).filter(Boolean);
  useSubscribe([und, ...keys]);
  const spotT = useTick(und);
  const ceOi = rows.reduce((a, r) => a + (ticks[r.CE]?.oi || 0), 0);
  const peOi = rows.reduce((a, r) => a + (ticks[r.PE]?.oi || 0), 0);
  const mp = rows.length ? maxPain(rows, ticks) : null;
  const heads = ["OI", "Vol", "IV", "Δ", "Θ", "LTP"];
  return (
    <div className="p-4 lg:p-6" data-testid="option-chain-page">
      <PageHeader title="Option chain">
        <select data-testid="chain-underlying-select" value={und} onChange={(e) => setUnd(e.target.value)} className={sel}>
          {unds.map((u) => <option key={u.underlying_key} value={u.underlying_key}>{u.symbol}</option>)}
        </select>
        <select data-testid="chain-expiry-select" value={expiry} onChange={(e) => setExpiry(e.target.value)} className={sel}>
          {expiries.map((x) => <option key={x}>{x}</option>)}
        </select>
        <select value={win} onChange={(e) => setWin(Number(e.target.value))} className={sel} data-testid="chain-window-select">
          {[8, 12, 20].map((n) => <option key={n} value={n}>±{n} strikes</option>)}
        </select>
      </PageHeader>
      <div className="flex flex-wrap gap-6 mb-3 text-[12px]">
        <span>Spot <b className={`num ${tone(spotT?.ch)}`} data-testid="chain-spot">{fmt(spot)}</b> <span className={`num ${tone(spotT?.ch)}`}>{pct(spotT?.chp)}</span></span>
        <span>PCR <b className="num" data-testid="chain-pcr">{ceOi ? (peOi / ceOi).toFixed(2) : "—"}</b></span>
        <span>Max pain <b className="num" data-testid="chain-max-pain">{mp ? fmt(mp) : "—"}</b></span>
        <span className="text-muted-foreground">Lot {lot} · Hover LTP to trade or add to basket</span>
      </div>
      <Panel>
        {!rows.length ? (chain ? <Empty testid="chain-empty">No option contracts found for this expiry.</Empty> : <SkeletonRows rows={10} cols={11} testid="chain-skeleton" />) : (
          <div className="overflow-auto thin-scroll max-h-[calc(100vh-260px)]">
            <table className="w-full text-[11.5px]" data-testid="option-chain-table">
              <thead className="sticky top-0 bg-card z-10 border-b border-border">
                <tr><th colSpan={6} className="eyebrow py-1.5 text-up">Calls</th><th className="eyebrow">Strike</th><th colSpan={6} className="eyebrow text-down">Puts</th></tr>
                <tr className="eyebrow">{heads.map((h) => <th key={`c${h}`} className={`px-2 py-1 text-right ${h === "Θ" ? "hidden xl:table-cell" : ""}`}>{h}</th>)}<th />
                  {[...heads].reverse().map((h) => <th key={`p${h}`} className={`px-2 py-1 text-right ${h === "Θ" ? "hidden xl:table-cell" : ""}`}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const spotLine = spot && rows[i + 1] && r.strike <= spot && rows[i + 1].strike > spot;
                  return (
                    <tr key={r.strike} data-testid={`chain-row-${r.strike}`} className={`h-9 border-b ${spotLine ? "border-b-2 border-b-primary" : "border-border/50"} hover:bg-accent/40`}>
                      <Side k={r.CE} itm={spot && r.strike < spot} />
                      <td className={`px-3 text-center num font-semibold ${r.strike === atm ? "bg-primary/15 text-primary" : "bg-muted/40"}`}>{fmt(r.strike)}</td>
                      <Side k={r.PE} itm={spot && r.strike > spot} right />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>)}
      </Panel>
    </div>
  );
}
